import { useState } from 'react';
import styled, { css } from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { InteractiveOnDeactivation } from '@elfsight/widget-deactivation';
import { useSettings } from '@wu/widget-core';
import { useRect } from 'react-use-rect';
import { AP_BUTTONS_MOBILE_WIDTH } from '../../../../constants';
import { getPlayerControls, useIsWaveformProgress } from '../../../../helpers';
import { Buttons } from '../../../buttons';
import {
  Deactivation,
  FREE_LINK_HEIGHT,
  Image,
  TrackInfo,
  useTheme,
  useWidth
} from '../../../common';
import { useSource } from '../../../sources';
import { usePlayer } from '../../PlayerProvider';
import {
  CloseButton,
  FakeTrack,
  HoverTime,
  Loop,
  MinimizeButton,
  Next,
  PlaylistButton,
  PlayPause,
  Prev,
  ProgressBar,
  ProgressBarLoaded,
  ProgressBarPlayed,
  Shuffle,
  Thumb,
  Track,
  VolumeComponent,
  Waveform
} from '../../player-parts';
import { useFloating } from '../floating';

const MAX_INT_32 = 2 ** 31 - 1;

export function Bar() {
  const { width } = useWidth();
  const [containerWidth, setContainerWidth] = useState(0);
  const { setHeight, height } = useFloating();
  const [containerRef] = useRect(
    ({ width, height }) => {
      setContainerWidth(width);
      setHeight?.(height);
    },
    {
      resize: true
    }
  );

  const { currentItem, items } = useSource();
  const {
    playerParts,
    coverImage,
    sourceType,
    closeButton,
    controls: derivedControls,
    existingPlayerControls,
    playerControls,
    minimizeButton,
    displayFreeLink,
    displayDeactivation
  } = useSettings();
  const { textColor, backgroundStyle } = useTheme();

  const showButtonsMobile = width <= AP_BUTTONS_MOBILE_WIDTH;
  const { image, title, link, buttons, hasAuthor, hasCaption, hasTitle } =
    currentItem ?? {};
  const { showPlayer } = usePlayer();

  const { url } = coverImage ?? {};

  const controls = getPlayerControls(
    derivedControls,
    existingPlayerControls,
    playerControls
  );

  const withImage = playerParts.includes('image');
  const withPlaylist = controls.includes('playlist');
  const withTrackControls = controls.includes('trackControls');
  const hasTrackInfo =
    hasAuthor || hasCaption || hasTitle || playerParts.includes('status');
  const withMainControlsWrapper =
    withTrackControls ||
    withImage ||
    (!showButtonsMobile && buttons?.length) ||
    hasTrackInfo;

  const withChangeTrackControls = sourceType === 'rss' || items?.length > 1;
  const withShuffleOrLoop =
    withChangeTrackControls &&
    (controls.includes('shuffle') || controls.includes('loop'));
  const withVolume = controls.includes('volume');
  const isWaveformProgress = useIsWaveformProgress();
  const withSecondaryControls =
    withShuffleOrLoop ||
    withVolume ||
    withPlaylist ||
    closeButton ||
    minimizeButton;

  const paddingBottom =
    displayFreeLink && !minimizeButton ? FREE_LINK_HEIGHT + 3 : null;

  if (!showPlayer) {
    return null;
  }

  return (
    <Deactivation
      childrenRef={containerRef}
      style={{ zIndex: MAX_INT_32 }}
      containerStyle={{ height }}
    >
      <Container
        _backgroundStyle={backgroundStyle}
        _color={textColor}
        _paddingBottom={paddingBottom}
      >
        <Component>
          <Show when={controls.includes('progressBar')}>
            <Show
              when={isWaveformProgress}
              fallback={<StyledProgressBar hideTime />}
            >
              <Waveform hideTime />
            </Show>
          </Show>

          {(withMainControlsWrapper || withSecondaryControls) && (
            <InfoContainer>
              {withMainControlsWrapper && (
                <Wrapper>
                  <LeftWrapper>
                    {withTrackControls && (
                      <ControlsMain>
                        {withChangeTrackControls && <Prev />}
                        <PlayPause />
                        {withChangeTrackControls && <Next />}
                      </ControlsMain>
                    )}

                    {withImage && (
                      <ImageContainer
                        trackName={title}
                        link={url || image}
                        key={link}
                      />
                    )}

                    <StyledTrackInfo />
                  </LeftWrapper>
                  {!showButtonsMobile && (
                    <Buttons containerWidth={containerWidth} />
                  )}
                </Wrapper>
              )}

              {withSecondaryControls && (
                <ControlsSecond>
                  {withChangeTrackControls && controls.includes('shuffle') && (
                    <Shuffle withoutMargin />
                  )}
                  {withChangeTrackControls && controls.includes('loop') && (
                    <Loop withoutMargin />
                  )}
                  {withVolume && <VolumeComponent withoutMargin />}
                  {withPlaylist && (
                    <InteractiveOnDeactivation
                      isInteractive={displayDeactivation}
                    >
                      <PlaylistButton withoutMargin />
                    </InteractiveOnDeactivation>
                  )}
                  {closeButton && !minimizeButton && (
                    <CloseButton withoutMargin />
                  )}
                  {minimizeButton && <MinimizeButton withoutMargin />}
                </ControlsSecond>
              )}
            </InfoContainer>
          )}

          {showButtonsMobile && <Buttons containerWidth={containerWidth} />}
        </Component>
      </Container>
    </Deactivation>
  );
}

const Container = styled.div<{
  _color: string;
  _paddingBottom: number | null;
  _backgroundStyle?: string;
}>`
  ${({ _color, _paddingBottom, _backgroundStyle }) => css`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    border-top: 2px solid rgba(255, 255, 255, 0.3);
    box-sizing: border-box;
    box-shadow: 0 0 10px 0 rgba(17, 17, 17, 0.2);
    z-index: 1000000000;
    padding: ${_paddingBottom ? `6px 12px ${_paddingBottom}px` : '6px 12px'};
    color: ${_color};

    ${_backgroundStyle};
  `}
`;

const Component = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  z-index: 999;
`;

const StyledProgressBar = styled(ProgressBar)`
  position: absolute;
  width: 100%;
  left: 0;
  top: 0;

  & ${HoverTime} {
    margin-top: -4px;
  }

  & ${Thumb} {
    top: 0;
  }

  & ${Track} {
    margin-top: -6px;
  }

  & ${ProgressBarPlayed} {
    border-radius: 0;
    top: 6px;
  }

  & ${ProgressBarLoaded} {
    border-radius: 0;
    top: 6px;
  }

  & ${FakeTrack} {
    border-radius: 0;
    top: 6px;
  }
`;

const InfoContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 100%;
  min-height: 48px;
  gap: 24px;
`;

const ImageContainer = styled(Image)`
  width: 48px;
  height: 48px;
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  margin-right: 12px;
`;

const ControlsMain = styled.div`
  width: auto;
  display: flex;
  align-items: center;
  margin-right: 24px;
`;

const ControlsSecond = styled.div`
  display: flex;
  align-self: flex-start;
  align-items: center;
  justify-content: center;
  min-width: fit-content;
  gap: 16px;
  margin: auto 0;
`;

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
`;

const LeftWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-right: 14px;
  max-width: 100%;
`;

const StyledTrackInfo = styled(TrackInfo)`
  max-width: 280px;
`;
